import { clearAuthToken, getAuthToken, getAuthTokenDecoded, isAuthTokenExpired } from "auth/authToken";
import { AuthMode } from "auth/types";
import { getAuthMode } from "config/config";
import { useCallback, useMemo } from "react";

export const useAuth = () => {
    const authMode = getAuthMode();

    const isAuthenticated = useCallback((): boolean => {
        if (authMode === AuthMode.NONE) {
            return true;
        }

        const token = getAuthToken();
        return !!token && !isAuthTokenExpired(token);
    }, [authMode]);

    const getUserName = useCallback(() => {
        if (authMode === AuthMode.NONE) {
            return "";
        }

        const token = getAuthTokenDecoded();
        if (!token) {
            return "";
        }

        return token?.unique_name ?? token?.sub ?? "";
    }, [authMode]);

    const roles = useMemo(() => {
        const token = getAuthTokenDecoded();
        return new Set(token?.roles ?? []);
    }, []);

    const hasRole = useCallback(
        (role: string) => {
            if (authMode === AuthMode.NONE) {
                return true;
            }

            return roles.has(role);
        },
        [roles, authMode],
    );

    const signOut = useCallback(() => {
        if (authMode === AuthMode.NONE) {
            return;
        }

        clearAuthToken();
    }, [authMode]);

    return { isAuthenticated, getUserName, signOut, hasRole };
};
